import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Trophy, Medal, Award, Compass, BookOpen, Target, Flame, Zap, Crown, Star } from 'lucide-react';

const ICON_MAP = { Compass, BookOpen, Target, Award, Flame, Zap, Crown, Star };
const COLOR_MAP = {
  blue: 'from-blue-400 to-blue-600',
  amber: 'from-amber-400 to-amber-600',
  rose: 'from-rose-400 to-rose-600',
  violet: 'from-violet-400 to-violet-600',
  orange: 'from-orange-400 to-orange-600',
  yellow: 'from-yellow-400 to-yellow-600',
  emerald: 'from-emerald-400 to-emerald-600',
  indigo: 'from-indigo-400 to-indigo-600',
};

const RankMedal = ({ rank }) => {
  if (rank === 1) return <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center shadow-md"><Trophy className="w-3.5 h-3.5 text-white" /></div>;
  if (rank === 2) return <div className="w-7 h-7 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center shadow-md"><Medal className="w-3.5 h-3.5 text-white" /></div>;
  if (rank === 3) return <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-md"><Medal className="w-3.5 h-3.5 text-white" /></div>;
  return <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">{rank}</div>;
};

const initials = (name = '') => name.split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';

/**
 * mode: "compact" (student dashboard sidebar — top 5 + me) | "full" (admin sidebar — all rows + filters)
 * variant: "right" or "left" (just for slight visual hint)
 */
export default function LeaderboardSidebar({ mode = 'compact', variant = 'right' }) {
  const [timeframe, setTimeframe] = useState('all_time');
  const [vertical, setVertical] = useState(''); // '' == All Verticals (default per user choice)
  const [availableVerticals, setAvailableVerticals] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load distinct verticals for chip filters
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/leaderboard/verticals');
        setAvailableVerticals(res.data?.verticals || []);
      } catch (e) { /* fine */ }
    })();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const limit = mode === 'compact' ? 10 : 100;
      const q = new URLSearchParams({ timeframe, limit: String(limit) });
      if (vertical) q.set('vertical', vertical);
      const res = await api.get(`/leaderboard?${q.toString()}`);
      setData(res.data);
    } catch (e) {
      console.error('leaderboard fetch fail', e);
    } finally {
      setLoading(false);
    }
  }, [timeframe, mode, vertical]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rows = data?.rows || [];
  const me = data?.me;
  const topRows = mode === 'compact' ? rows.slice(0, 5) : rows;
  const meInTop = me && topRows.some(r => r.student_id === me.student_id);

  return (
    <aside className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden ${mode === 'compact' ? 'w-full' : 'w-full'}`} data-testid="leaderboard-sidebar">
      <div className="px-4 py-3 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white flex items-center gap-2">
        <Trophy className="w-4 h-4" />
        <h3 className="font-bold text-sm">Leaderboard</h3>
      </div>

      <div className="px-3 pt-3">
        <Tabs value={timeframe} onValueChange={setTimeframe} className="w-full">
          <TabsList className="grid grid-cols-2 w-full h-8">
            <TabsTrigger value="all_time" className="text-[11px]" data-testid="lb-tab-all-time">All Time</TabsTrigger>
            <TabsTrigger value="this_month" className="text-[11px]" data-testid="lb-tab-month">This Month</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Vertical filter chips */}
      {availableVerticals.length > 0 && (
        <div className="px-3 pt-2" data-testid="lb-vertical-chips">
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => setVertical('')}
              className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition ${vertical === '' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-400'}`}
              data-testid="lb-chip-all"
            >All</button>
            {availableVerticals.map(v => (
              <button
                key={v}
                type="button"
                onClick={() => setVertical(v)}
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition ${vertical === v ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-400'}`}
                data-testid={`lb-chip-${v}`}
              >{v}</button>
            ))}
          </div>
        </div>
      )}

      {/* My Rank chip (student view) */}
      {me && (
        <div className="px-3 pt-3">
          <div className="px-3 py-2 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RankMedal rank={me.rank} />
              <div>
                <div className="text-[11px] text-slate-500 leading-none">My Rank{vertical ? ` · ${vertical}` : ''}</div>
                <div className="text-sm font-bold text-slate-900">{me.name}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-indigo-600 leading-none">{me.completed}</div>
              <div className="text-[10px] text-slate-500">classes</div>
            </div>
          </div>
        </div>
      )}

      {/* Rows */}
      <div className="px-3 py-3 space-y-1.5 max-h-[480px] overflow-y-auto">
        {loading && <div className="text-xs text-slate-400 text-center py-4">Loading…</div>}
        {!loading && topRows.length === 0 && <div className="text-xs text-slate-400 text-center py-4">No rankings yet{vertical ? ` in ${vertical}` : ''}.</div>}
        {topRows.map(r => {
          const isMe = me && r.student_id === me.student_id;
          return (
            <div key={r.student_id}
                 className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition ${isMe ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-slate-50'}`}
                 data-testid={`lb-row-${r.student_id}`}>
              <RankMedal rank={r.rank} />
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-[10px] font-bold text-slate-600 flex-shrink-0">{initials(r.name)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-900 truncate">{r.name}</div>
                <div className="text-[10px] text-slate-500 truncate">{r.citt_id}{r.grade ? ` · Gr ${r.grade}` : ''}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-slate-900 leading-none">{r.completed}</div>
                {r.purchased > 0 && timeframe === 'all_time' && (
                  <div className="text-[10px] text-slate-400">/{r.purchased}</div>
                )}
              </div>
            </div>
          );
        })}
        {/* If "me" not in top, show a chip at the bottom */}
        {mode === 'compact' && me && !meInTop && (
          <>
            <div className="text-center text-[10px] text-slate-400 py-1">⋯</div>
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-blue-50 ring-1 ring-blue-200">
              <RankMedal rank={me.rank} />
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-300 to-blue-500 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">{initials(me.name)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-900 truncate">{me.name} <span className="text-[10px] text-blue-600">(you)</span></div>
              </div>
              <div className="text-sm font-bold text-slate-900">{me.completed}</div>
            </div>
          </>
        )}
      </div>

      {/* Badge progress (student view) */}
      {me && me.next_badge && (
        <div className="px-3 pb-3">
          <div className="rounded-xl border border-slate-200 p-3 bg-gradient-to-br from-slate-50 to-white">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1.5">Next Badge</div>
            <div className="flex items-center gap-2.5">
              {(() => {
                const Icon = ICON_MAP[me.next_badge.icon] || Award;
                return (
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${COLOR_MAP[me.next_badge.color] || 'from-slate-400 to-slate-600'} flex items-center justify-center shadow-md opacity-50`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                );
              })()}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-slate-900 truncate">{me.next_badge.label}</div>
                <div className="text-[10px] text-slate-500">{me.next_badge.remaining} more class{me.next_badge.remaining !== 1 ? 'es' : ''}</div>
                {/* Progress bar */}
                <div className="mt-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all"
                       style={{ width: `${Math.min(100, ((me.completed % me.next_badge.threshold) / me.next_badge.threshold) * 100)}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Earned badges (student view) */}
      {me && me.badges && me.badges.length > 0 && (
        <div className="px-3 pb-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1.5">My Badges ({me.badges.length})</div>
          <div className="flex flex-wrap gap-1.5">
            {me.badges.map(b => {
              const Icon = ICON_MAP[b.icon] || Award;
              return (
                <div key={b.id}
                     title={b.label}
                     className={`w-8 h-8 rounded-lg bg-gradient-to-br ${COLOR_MAP[b.color] || 'from-slate-400 to-slate-600'} flex items-center justify-center shadow-md transition hover:scale-110`}
                     data-testid={`badge-${b.id}`}>
                  <Icon className="w-3.5 h-3.5 text-white" />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
}
